import React, { useState, useEffect } from 'react';
import { useBookings } from '../../hooks/useBookings';
import { useBookingActions } from '../../hooks/useBookingActions';
import { useDashboardChanges } from '../../hooks/useDashboardChanges';
import { VIEW_MODES, BOOKING_STATUSES, CALENDAR_VIEWS, SORT_OPTIONS } from '../../constants/statuses';
import FilterTabs from './FilterTabs';
import BookingList from './BookingList';
import CalendarView from './CalendarView';
import LoadingSpinner from '../common/LoadingSpinner';
import PopupNotification from '../common/PopupNotification';
import WhatsNewNotification from '../common/WhatsNewNotification';
import DatePickerModal from '../common/DatePickerModal';

// Helper functions for localStorage
const getStoredStatusFilters = () => {
  try {
    const stored = localStorage.getItem('wwoof-booking-status-filters');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that the stored values are valid statuses
      const validStatuses = Object.values(BOOKING_STATUSES);
      const filteredStatuses = parsed.filter(status => validStatuses.includes(status));
      return filteredStatuses.length > 0 ? filteredStatuses : Object.values(BOOKING_STATUSES);
    }
  } catch (error) {
    console.warn('Failed to load status filters from localStorage:', error);
  }
  return Object.values(BOOKING_STATUSES);
};

const storeStatusFilters = (statuses) => {
  try {
    localStorage.setItem('wwoof-booking-status-filters', JSON.stringify(statuses));
  } catch (error) {
    console.warn('Failed to save status filters to localStorage:', error);
  }
};

// Helper functions for view mode persistence
const getStoredViewMode = (fallbackView = VIEW_MODES.LIST) => {
  try {
    const stored = localStorage.getItem('wwoof-booking-view-mode');
    if (stored && Object.values(VIEW_MODES).includes(stored)) {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to load view mode from localStorage:', error);
  }
  return fallbackView;
};

const storeViewMode = (viewMode) => {
  try {
    localStorage.setItem('wwoof-booking-view-mode', viewMode);
  } catch (error) {
    console.warn('Failed to save view mode to localStorage:', error);
  }
};

// Helper functions for sort preference persistence
const getStoredSort = () => {
  try {
    const stored = localStorage.getItem('wwoof-booking-sort-by');
    if (stored && Object.values(SORT_OPTIONS).includes(stored)) {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to load sort preference from localStorage:', error);
  }
  return SORT_OPTIONS.CREATED_DESC;
};

const storeSort = (sortBy) => {
  try {
    localStorage.setItem('wwoof-booking-sort-by', sortBy);
  } catch (error) {
    console.warn('Failed to save sort preference to localStorage:', error);
  }
};

const BookingDashboard = ({ userId, view: initialView = VIEW_MODES.LIST }) => {
  // Determine the initial view mode - use stored preference if available, 
  // otherwise fall back to initialView, but ignore 'both' value from PHP
  const getInitialView = () => {
    const storedView = getStoredViewMode();
    if (storedView && Object.values(VIEW_MODES).includes(storedView)) {
      return storedView;
    }
    // If initialView is 'both' or not a valid view mode, default to LIST
    if (initialView === 'both' || !Object.values(VIEW_MODES).includes(initialView)) {
      return VIEW_MODES.LIST;
    }
    return initialView;
  };

  const [view, setView] = useState(getInitialView);
  const [calendarView, setCalendarView] = useState(CALENDAR_VIEWS.MONTH);
  const [calendarModal, setCalendarModal] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState(getStoredStatusFilters);
  const [sortBy, setSortBy] = useState(getStoredSort);

  const { bookings, loading, refreshing, error, refreshBookings, currentUserId } = useBookings(userId);

  // Dashboard changes tracking
  const { 
    changes, 
    loading: changesLoading, 
    showNotification: showWhatsNew, 
    dismissNotification: dismissWhatsNew 
  } = useDashboardChanges(userId);

  // Save status filter preferences whenever they change
  useEffect(() => {
    storeStatusFilters(selectedStatuses);
  }, [selectedStatuses]);

  // Save view mode preference whenever it changes
  useEffect(() => {
    storeViewMode(view);
  }, [view]);

  // Save sort preference whenever it changes
  useEffect(() => {
    storeSort(sortBy);
  }, [sortBy]);

  // Handler to update status filters and save to localStorage
  const handleStatusChange = (newStatuses) => {
    setSelectedStatuses(newStatuses);
  };

  // Handler to update view mode and save to localStorage
  const handleViewChange = (newView) => {
    setView(newView);
  };

  // Handler to update sort preference
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };
  
  const {
    actionLoading,
    error: actionError,
    acceptBooking,
    cancelBooking,
    editBookingDates,
    setError: setActionError,
    showAcceptPopup,
    setShowAcceptPopup,
    showAgreementPopup,
    setShowAgreementPopup,
    handleAcceptAgreement,
    handleAgreementClose,
    pendingBooking,
    showDatePicker,
    bookingToEdit,
    handleDatePickerSave,
    handleDatePickerClose,
    showErrorPopup,
    setShowErrorPopup,
    errorPopupMessage,
  } = useBookingActions(refreshBookings);

  // Filter bookings based on selected status only
  const filterBookings = (bookings) => {
    return bookings.filter(booking => 
      selectedStatuses.includes(booking.status)
    );
  };

  // Sort bookings based on selected sort option
  const sortBookings = (bookings) => {
    const sorted = [...bookings];
    switch (sortBy) {
      case SORT_OPTIONS.DATE_ASC:
        return sorted.sort((a, b) => a.start_date.localeCompare(b.start_date));
      case SORT_OPTIONS.DATE_DESC:
        return sorted.sort((a, b) => b.start_date.localeCompare(a.start_date));
      case SORT_OPTIONS.CREATED_ASC:
        return sorted.sort((a, b) => a.created_at.localeCompare(b.created_at));
      case SORT_OPTIONS.CREATED_DESC:
        return sorted.sort((a, b) => b.created_at.localeCompare(a.created_at));
      default:
        return sorted.sort((a, b) => a.start_date.localeCompare(b.start_date));
    }
  };

  const sortedBookings = sortBookings(filterBookings(bookings));

  if (loading || changesLoading) {
    return <LoadingSpinner message='Loading bookings...' />;
  }

  return (
    <div className='wwoof-booking-dashboard'>
      {/* What's New Notification */}
      <WhatsNewNotification
        changes={changes?.changes}
        isVisible={showWhatsNew}
        onClose={dismissWhatsNew}
        lastVisit={changes?.last_visit}
      />
      
      <FilterTabs 
        view={view}
        setView={handleViewChange}
        calendarView={calendarView}
        setCalendarView={setCalendarView}
        selectedStatuses={selectedStatuses}
        onStatusChange={handleStatusChange}
        sortBy={sortBy}
        onSortChange={handleSortChange}
      />
      
      {/* Show error from useBookings (like API/loading errors) inline */}
      {error && (
        <div className='error-message'>
          {error}
        </div>
      )}
      
      {/* Show all action errors via popup notification */}
      <PopupNotification
        message={errorPopupMessage}
        isVisible={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
      />

      {view === VIEW_MODES.LIST ? (
        <BookingList
          bookings={sortedBookings}
          loading={refreshing}
          actionLoading={actionLoading}
          onEditDates={editBookingDates}
          onCancel={cancelBooking}
          onAccept={acceptBooking}
          currentUserId={currentUserId}
        />
      ) : (
        <CalendarView
          bookings={sortedBookings}
          calendarModal={calendarModal}
          setCalendarModal={setCalendarModal}
          calendarView={calendarView}
          actionLoading={actionLoading}
          onEditDates={editBookingDates}
          onCancel={cancelBooking}
          onAccept={acceptBooking}
          currentUserId={currentUserId}
          refreshing={refreshing}
        />
      )}

      {/* Agreement popup for booking acceptance */}
      <PopupNotification 
        message={
          pendingBooking?.initiated_by === 'wwoofer'
            ? (window.wwoofBooking?.popupTexts?.hostAccepted || "Before accepting this WWOOFer's visit request, please confirm that you understand and agree to the following:")
            : (window.wwoofBooking?.popupTexts?.wwoofrerAccepted || "Before accepting this visit request, please confirm that you understand and agree to the following:")
        }
        isVisible={showAgreementPopup}
        onClose={handleAgreementClose}
        requireAgreement={true}
        agreementText="I agree to the WWOOF Ireland Declaration."
        onAccept={handleAcceptAgreement}
      />

      <PopupNotification 
        message={window.wwoofBooking?.popupTexts?.wwoofrerAccepted || 'You have accepted this visit request!'}
        isVisible={showAcceptPopup}
        onClose={() => setShowAcceptPopup(false)}
      />

      <DatePickerModal
        isOpen={showDatePicker}
        onClose={handleDatePickerClose}
        onSave={handleDatePickerSave}
        booking={bookingToEdit}
        loading={actionLoading}
        error={actionError}
      />
    </div>
  );
};

export default BookingDashboard;
